import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { OpeningBalanceDialog } from '@/components/opening-balance-dialog';
import { EmptyState } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { takesOpeningBalance, type Account } from '@/lib/accounts';
import { api } from '@/lib/api';
import { formatAmount, formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useOrgRole } from '@/lib/portal';
import { sourceLabel } from '@/lib/journal';
import { nameFor } from '@/lib/arabic';
import { useLanguage, useT } from '@/lib/i18n';

interface RegisterLine {
  entryId: string;
  entryNo: string;
  date: string;
  sourceType: string;
  memo: string | null;
  description: string | null;
  contactName: string | null;
  otherAccount: string;
  debit: string | null;
  credit: string | null;
  balance: string;
  /** Foreign Bank and Credit card accounts: the line in the account's own currency. */
  foreignDebit?: string | null;
  foreignCredit?: string | null;
  foreignBalance?: string | null;
  status: 'posted' | 'reversed';
}

interface Register {
  account: Account;
  from: string | null;
  to: string | null;
  openingBalance: string;
  closingBalance: string;
  foreignOpeningBalance?: string | null;
  foreignClosingBalance?: string | null;
  lines: RegisterLine[];
}

interface Search {
  from?: string;
  to?: string;
}

export const Route = createFileRoute('/accounting/chart-of-accounts/$accountId/register')({
  component: AccountRegister,
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(typeof s['from'] === 'string' ? { from: s['from'] } : {}),
    ...(typeof s['to'] === 'string' ? { to: s['to'] } : {}),
  }),
});

type ColumnLabel = 'deposit' | 'payment' | 'increase' | 'decrease';

/** Columns per account type (document 05, Register): banks show Payment/Deposit. */
function columnLabels(a: Account): { dr: ColumnLabel; cr: ColumnLabel } {
  if (a.accountType === 'bank' || a.accountType === 'credit_card')
    return { dr: 'deposit', cr: 'payment' };
  if (a.class === 'asset' || a.class === 'expense') return { dr: 'increase', cr: 'decrease' };
  return { dr: 'decrease', cr: 'increase' };
}

/** Screen A2 (document 06): account register with running balance. */
function AccountRegister() {
  const { accountId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const orgId = useOrgId();
  const qs = new URLSearchParams({
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
  }).toString();
  const reg = useQuery({
    queryKey: ['register', orgId, accountId, search.from ?? '', search.to ?? ''],
    queryFn: () =>
      api<Register>(`/orgs/${orgId}/accounts/${accountId}/register${qs ? `?${qs}` : ''}`),
    enabled: Boolean(orgId),
  });
  const role = useOrgRole();
  const [opening, setOpening] = useState(false);
  const t = useT();
  const lang = useLanguage();

  if (reg.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (reg.isError)
    return <EmptyState title={t('accountingPages.register.loadError')} hint={reg.error.message} />;
  const { account, lines } = reg.data;
  const labels = columnLabels(account);
  const foreign = account.foreignBalance !== null ? account.accountCurrency : null;
  // a debit increases a debit-normal account; show amounts in the column that matches
  const drIsIncrease = account.class === 'asset' || account.class === 'expense';

  return (
    <>
      <Link
        to="/accounting/chart-of-accounts"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="rtl:-scale-x-100 size-4" /> {t('accountingPages.register.back')}
      </Link>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[1.7rem] font-medium tracking-[-0.01em]">
            {account.code} · <bdi>{nameFor(lang, account.fullName, account.fullNameAr)}</bdi>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {nameFor(lang, account.accountTypeName, account.accountTypeNameAr)} ·{' '}
            {nameFor(lang, account.detailTypeName, account.detailTypeNameAr)}
            {!account.isActive ? ` · ${t('common.inactive')}` : ''}
          </p>
        </div>
        <div className="text-end">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('accountingPages.register.endingBalance')}
          </div>
          <div className="tnum text-2xl font-semibold">
            {account.currency} {formatAmount(reg.data.closingBalance)}
          </div>
        </div>
      </div>
      <div className="mb-3 flex items-end gap-3">
        <label className="flex flex-col gap-1 text-sm font-medium">
          {t('accountingPages.register.from')}
          <DateInput
            className="w-[160px]"
            value={search.from ?? ''}
            onChange={(from) => void navigate({ search: (s: Search) => ({ ...s, from }) })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          {t('accountingPages.register.to')}
          <DateInput
            className="w-[160px]"
            value={search.to ?? ''}
            onChange={(to) => void navigate({ search: (s: Search) => ({ ...s, to }) })}
          />
        </label>
        {search.from || search.to ? (
          <Button variant="ghost" onClick={() => void navigate({ search: {} })}>
            {t('accountingPages.register.clearDates')}
          </Button>
        ) : null}
        <div className="ms-auto flex gap-2">
          {role !== null && role !== 'client_viewer' && takesOpeningBalance(account) ? (
            <Button variant="outline" onClick={() => setOpening(true)}>
              {t('accountingPages.register.setOpeningBalance')}
            </Button>
          ) : null}
          <Button variant="outline" asChild>
            <Link to="/accounting/journal-entries/new">
              {t('accountingPages.register.addJournalEntry')}
            </Link>
          </Button>
        </div>
      </div>
      <OpeningBalanceDialog
        account={opening ? { ...account, name: nameFor(lang, account.name, account.nameAr) } : null}
        onClose={() => setOpening(false)}
      />
      {lines.length === 0 ? (
        <EmptyState title={t('accountingPages.register.empty')} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">{t('accountingPages.register.colDate')}</TableHead>
                <TableHead className="w-[80px]">{t('accountingPages.register.colRefNo')}</TableHead>
                <TableHead className="w-[120px]">{t('accountingPages.register.colType')}</TableHead>
                <TableHead>{t('accountingPages.register.colPayee')}</TableHead>
                <TableHead>{t('accountingPages.register.colAccount')}</TableHead>
                <TableHead>{t('accountingPages.register.colMemo')}</TableHead>
                <TableHead className="w-[130px] text-end">
                  {t(`accountingPages.register.${drIsIncrease ? labels.cr : labels.dr}`)}
                </TableHead>
                <TableHead className="w-[130px] text-end">
                  {t(`accountingPages.register.${drIsIncrease ? labels.dr : labels.cr}`)}
                </TableHead>
                <TableHead className="w-[140px] text-end">
                  {foreign
                    ? t('accountingPages.register.balanceIn', {
                        currency: reg.data.account.currency,
                      })
                    : t('accountingPages.register.balance')}
                </TableHead>
                {foreign ? (
                  <>
                    <TableHead className="w-[130px] text-end">
                      {t('accountingPages.register.amountIn', { currency: foreign })}
                    </TableHead>
                    <TableHead className="w-[140px] text-end">
                      {t('accountingPages.register.balanceIn', { currency: foreign })}
                    </TableHead>
                  </>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {reg.data.from ? (
                <TableRow className="text-muted-foreground">
                  <TableCell colSpan={8}>{t('accountingPages.register.openingBalance')}</TableCell>
                  <TableCell className="tnum text-end">
                    {formatAmount(reg.data.openingBalance)}
                  </TableCell>
                  {foreign ? (
                    <>
                      <TableCell />
                      <TableCell className="tnum text-end">
                        {formatAmount(reg.data.foreignOpeningBalance ?? '0.00')}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              ) : null}
              {lines.map((l, i) => (
                <TableRow
                  key={`${l.entryId}-${i}`}
                  className="cursor-pointer"
                  onClick={() =>
                    void navigate({
                      to: '/accounting/journal-entries/$entryId',
                      params: { entryId: l.entryId },
                    })
                  }
                >
                  <TableCell>{formatDate(l.date)}</TableCell>
                  <TableCell className="tnum">{l.entryNo}</TableCell>
                  <TableCell>
                    {sourceLabel(l.sourceType)}
                    {l.status === 'reversed' ? (
                      <Badge variant="outline" className="ms-1">
                        {t('accountingPages.register.reversed')}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>{l.contactName ?? ''}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{l.otherAccount}</TableCell>
                  <TableCell className="max-w-[260px] truncate">
                    {l.description ?? l.memo ?? ''}
                  </TableCell>
                  <TableCell className="tnum text-end">
                    {formatAmount(drIsIncrease ? l.credit : l.debit)}
                  </TableCell>
                  <TableCell className="tnum text-end">
                    {formatAmount(drIsIncrease ? l.debit : l.credit)}
                  </TableCell>
                  <TableCell className="tnum text-end">{formatAmount(l.balance)}</TableCell>
                  {foreign ? (
                    <>
                      <TableCell className="tnum text-end">
                        {l.foreignDebit
                          ? formatAmount(l.foreignDebit)
                          : l.foreignCredit
                            ? `-${formatAmount(l.foreignCredit)}`
                            : ''}
                      </TableCell>
                      <TableCell className="tnum text-end">
                        {formatAmount(l.foreignBalance ?? '0.00')}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
